import Cropper from 'cropperjs';
import { CapacitorHttp } from '@capacitor/core';

import { getCurrentUserToken } from '../../../../../auth/authService';
import { getIsMilanoteDomain, isAlternateWebHost } from '../../../../../../common/utils/urlUtil';

// Matches the JPEG/WebP export quality already used elsewhere in the image editor flow.
// We keep this slightly below 1.0 to avoid oversized uploads without a visible quality drop.
export const DEFAULT_LOSSY_IMAGE_QUALITY = 0.85;
const CROPPER_RESTRICT_TO_CANVAS_VIEW_MODE = 2;

type CroppedImageBlobOptions = {
    imageUrl: string;
    inputImageType: string;
    outputImageType: string;
    imageEditorData: Cropper.Data;
};

/**
 * Build bearer-token headers for image downloads that are allowed to use Milanote authentication.
 */
const getAuthenticatedHeaders = (
    token?: string,
):
    | {
          Authorization: string;
      }
    | undefined =>
    token
        ? {
              Authorization: `Bearer ${token}`,
          }
        : undefined;

/**
 * Fetch image bytes through the browser fetch API when standard web CORS/auth handling is available.
 */
const fetchImageResponse = (url: string, token?: string): Promise<Response> =>
    fetch(url, {
        headers: getAuthenticatedHeaders(token),
    });

/**
 * Only Milanote-owned hosts should receive Milanote bearer tokens.
 */
const shouldFetchWithAuthentication = (url: string): boolean => getIsMilanoteDomain(url) || isAlternateWebHost(url);

/**
 * Download image bytes for editing or background removal.
 * Try public image URLs without auth first, then retry Milanote-owned hosts with auth.
 */
export const fetchImageBlobFromUrl = async (url: string): Promise<Blob> => {
    const token = getCurrentUserToken();
    const response = await fetchImageResponse(url);

    if (response.ok) return response.blob();
    if (!token || !shouldFetchWithAuthentication(url)) throw new Error(`Failed to fetch image (${response.status})`);

    const authenticatedResponse = await fetchImageResponse(url, token);

    if (authenticatedResponse.ok) return authenticatedResponse.blob();

    throw new Error(`Failed to fetch image (${authenticatedResponse.status})`);
};

/**
 * Download image bytes through Capacitor for editor flows that need a local object URL on mobile.
 */
export const fetchImageUsingCapacitorHTTP = async (
    imageUrl: string,
    imageType: string,
): Promise<{ objectUrl: string }> => {
    try {
        const blob = await fetchImageBlobFromUrl(imageUrl);
        return { objectUrl: URL.createObjectURL(blob) };
    } catch {
        const token = getCurrentUserToken();
        const shouldSendAuthentication = !!token && shouldFetchWithAuthentication(imageUrl);
        const response = await CapacitorHttp.get({
            url: imageUrl,
            responseType: 'arraybuffer',
            headers: getAuthenticatedHeaders(shouldSendAuthentication ? token : undefined),
        });

        const base64Data = response.data;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: imageType });
        const objectUrl = URL.createObjectURL(blob);

        return { objectUrl };
    }
};

/**
 * Draw an image object URL into a canvas so the editor can read a data URL for Cropper.
 */
export const loadImageIntoCanvas = (objectUrl: string, imageType: string): Promise<{ dataUrl: string }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('Unable to load image into canvas'));
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL(imageType);

            resolve({ dataUrl });
        };

        img.onerror = reject;
        img.src = objectUrl;
        img.alt = 'To edit';
    });
};

/**
 * Create hidden DOM nodes for Cropper, which needs a mounted image to calculate crop bounds.
 */
const createOffscreenContainer = (): { container: HTMLDivElement; image: HTMLImageElement } => {
    const container = document.createElement('div');
    // Keep the image mounted for Cropper measurements without ever showing it in the viewport.
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '-10000px';

    const image = document.createElement('img');
    container.appendChild(image);
    document.body.appendChild(container);

    return { container, image };
};

/**
 * Load an image source into an existing image element before Cropper reads its dimensions.
 */
const loadImageSourceIntoImage = (image: HTMLImageElement, imageSource: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = reject;
        image.src = imageSource;
    });
};

/**
 * Apply saved editor crop data to a mounted image and export the cropped result as a blob.
 */
const cropImageToBlob = (
    image: HTMLImageElement,
    imageEditorData: Cropper.Data,
    outputImageType: string,
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const cropper = new Cropper(image, {
            // Keep the crop selection fully inside the rendered image bounds.
            viewMode: CROPPER_RESTRICT_TO_CANVAS_VIEW_MODE,
            autoCrop: true,
            autoCropArea: 1,
            checkOrientation: false,
            ready: () => {
                cropper.setData(imageEditorData);
                cropper
                    .getCroppedCanvas({
                        width: imageEditorData.width,
                        height: imageEditorData.height,
                        maxWidth: 4096,
                        maxHeight: 4096,
                    })
                    .toBlob(
                        (blob: Blob | null) => {
                            cropper.destroy();
                            if (!blob) return reject(new Error('Failed to produce cropped image blob'));
                            resolve(blob);
                        },
                        outputImageType,
                        DEFAULT_LOSSY_IMAGE_QUALITY,
                    );
            },
        });
    });
};

/**
 * Download an image, mount it offscreen, and return the saved crop area as a new image blob.
 */
export const getCroppedImageBlobFromUrl = async ({
    imageUrl,
    inputImageType,
    outputImageType,
    imageEditorData,
}: CroppedImageBlobOptions): Promise<Blob> => {
    const { objectUrl } = await fetchImageUsingCapacitorHTTP(imageUrl, inputImageType);
    const { container, image } = createOffscreenContainer();

    try {
        await loadImageSourceIntoImage(image, objectUrl);
        return await cropImageToBlob(image, imageEditorData, outputImageType);
    } finally {
        URL.revokeObjectURL(objectUrl);
        container.remove();
    }
};
